import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { BACKEND } from "../api";
import { useTags, TagChips, KindBadge, Modal } from "../components/ui";
import { WhatsAppButton } from "../components/WhatsAppButton";
import QuickActions from "../components/QuickActions";
import CompanyNotesTasks from "../components/CompanyNotesTasks";
import CompanyComms from "../components/CompanyComms";
import SentComms from "../components/SentComms";
import ClientFiles from "../components/ClientFiles";
import { ReviewRequestButton } from "../components/ReviewRequest";
import Contact360 from "../components/Contact360";

export default function CompanyDetail() {
  const { id } = useParams();
  const { tags } = useTags();
  const [data, setData] = useState(null);
  const [docs, setDocs] = useState([]);
  const [services, setServices] = useState(null);
  const [allPersons, setAllPersons] = useState([]);
  const [linkModal, setLinkModal] = useState(false);
  const [linkPerson, setLinkPerson] = useState("");
  const [linkRole, setLinkRole] = useState("");
  const [upFile, setUpFile] = useState(null);
  const [report, setReport] = useState(null);
  const [repBusy, setRepBusy] = useState(false);
  const navigate = useNavigate();
  const [mergeModal, setMergeModal] = useState(false);
  const [mergeCompanies, setMergeCompanies] = useState([]);
  const [mergeTarget, setMergeTarget] = useState("");
  const [mergeCreatePerson, setMergeCreatePerson] = useState(true);
  const [merging, setMerging] = useState(false);
  const [senderLinks, setSenderLinks] = useState([]);
  const [slPattern, setSlPattern] = useState("");
  const [slNote, setSlNote] = useState("");

  const loadSenderLinks = useCallback(() => {
    api.get(`/companies/${id}/sender-links`).then((r) => setSenderLinks(r.data)).catch(() => {});
  }, [id]);
  useEffect(() => { loadSenderLinks(); }, [loadSenderLinks]);

  const addSenderLink = async () => {
    if (!slPattern.trim()) return;
    try {
      const r = await api.post(`/companies/${id}/sender-links`, { pattern: slPattern, note: slNote });
      setSlPattern(""); setSlNote(""); loadSenderLinks();
      alert(`✅ Collegato "${r.data.pattern}". Email già presenti ricollegate a questo cliente: ${r.data.email_ricollegate}. Le prossime arriveranno già collegate.`);
    } catch (e) { alert(e?.response?.data?.detail || e.message); }
  };
  const delSenderLink = async (lid) => {
    if (!window.confirm("Rimuovere il collegamento? Le prossime email di questo mittente non verranno più collegate al cliente (quelle già collegate restano).")) return;
    await api.delete(`/companies/sender-links/${lid}`); loadSenderLinks();
  };

  const load = useCallback(() => {
    api.get(`/companies/${id}`).then((r) => setData(r.data)).catch(() => setData(null));
    api.get(`/documents?company_id=${id}`).then((r) => setDocs(r.data)).catch(() => {});
    api.get(`/companies/${id}/services`).then((r) => setServices(r.data)).catch(() => {});
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/persons").then((r) => setAllPersons(r.data)).catch(() => {}); }, []);

  const toggleReview = async (val) => {
    try { await api.put(`/companies/${id}`, { review_given: val }); load(); }
    catch (e) { alert("Errore: " + (e.response?.data?.detail || e.message)); }
  };

  if (!data) return <p className="muted">Caricamento…</p>;
  const { company, people, projects } = data;

  const addLink = async () => {
    if (!linkPerson) return;
    try {
      await api.post("/persons/links", { person_id: linkPerson, company_id: id, role: linkRole || null });
      setLinkModal(false); setLinkPerson(""); setLinkRole(""); load();
    } catch (e) { alert("Errore: " + (e.response?.data?.detail || e.message)); }
  };
  const removeLink = async (linkId) => { await api.delete(`/persons/links/${linkId}`); load(); };

  const upload = async () => {
    if (!upFile) return;
    const fd = new FormData();
    fd.append("company_id", id);
    fd.append("file", upFile);
    await api.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
    setUpFile(null); load();
  };
  const removeDoc = async (docId) => { if (window.confirm("Eliminare il documento?")) { await api.delete(`/documents/${docId}`); load(); } };

  const openMerge = async () => {
    try { const r = await api.get("/companies"); setMergeCompanies(r.data.filter((c) => c.id !== id)); } catch { setMergeCompanies([]); }
    setMergeTarget(""); setMergeModal(true);
  };
  const doMerge = async () => {
    if (!mergeTarget) return;
    const tname = mergeCompanies.find((c) => c.id === mergeTarget)?.name || "";
    if (!window.confirm(`Unire definitivamente "${company.name}" in "${tname}"? Fatture, email, F24, cartelle e documenti verranno spostati e questa azienda sarà eliminata.`)) return;
    setMerging(true);
    try {
      await api.post(`/companies/${id}/merge`, { target_id: mergeTarget, create_person: mergeCreatePerson });
      navigate(`/aziende/${mergeTarget}`);
    } catch (e) { alert("Errore: " + (e.response?.data?.detail || e.message)); }
    finally { setMerging(false); }
  };

  const openReport = async () => {
    try { const r = await api.get(`/stats/activity-report/${id}`); setReport(r.data); }
    catch (e) { alert("Errore: " + (e.response?.data?.detail || e.message)); }
  };
  const sendReport = async () => {
    if (!report?.to?.trim()) { alert("Inserisci l'email del destinatario."); return; }
    setRepBusy(true);
    try {
      await api.post(`/stats/activity-report/${id}/send`, { to: report.to.trim() });
      alert("Report attività inviato a " + report.to.trim());
      setReport(null);
    } catch (e) { alert("Errore invio: " + (e.response?.data?.detail || e.message)); }
    finally { setRepBusy(false); }
  };

  return (
    <div>
      <p><Link to="/aziende">← Aziende</Link></p>
      <h1 className="page-title">{company.name} <KindBadge kind={company.kind} /></h1>
      <p className="page-sub"><TagChips tagIds={company.tag_ids} tags={tags} /></p>

      <div className="toolbar" style={{ marginBottom: 8 }} data-testid="company-quick-actions">
        <QuickActions target={{ kind: "company", id: company.id, name: company.name, email: company.email, phone: company.phone, company: company.name }} />
        <Link className="btn sm" to={`/da-gestire?cerca=${encodeURIComponent(company.name)}`} data-testid="company-emails-search-btn" title="Cerca tutte le email e gli allegati che riguardano questo cliente (anche da consulenti e terzi)">📧 Email & allegati</Link>
        <div className="spacer" />
        <button className="btn sm" onClick={openMerge} data-testid="merge-company-btn" title="Sposta fatture, email, F24 e cartelle su un'altra azienda ed elimina questa">⇄ Unisci in un'altra azienda</button>
        <button className="btn sm danger" data-testid="company-delete-btn" onClick={async () => {
          if (!window.confirm(`Eliminare "${company.name}" con i suoi progetti e documenti? Le email ricevute non vengono toccate.`)) return;
          try { await api.delete(`/companies/${company.id}`); navigate("/contatti"); }
          catch (e) { alert("Errore: " + (e?.response?.data?.detail || e.message)); }
        }}>🗑 Elimina</button>
      </div>

      <Contact360 email={company.email || company.pec} phone={company.phone} name={company.name} companyId={company.id} hide={["companies"]} />

      <div className="panel" data-testid="sender-links-panel">
        <h3 style={{ marginTop: 0 }}>🔗 Mittenti collegati <span className="muted" style={{ fontSize: 13, fontWeight: 400 }}>(consulenti, studi paghe, terzi che scrivono per questo cliente)</span></h3>
        <p className="muted" style={{ fontSize: 12.5, marginTop: 4 }}>
          Le email di questi mittenti vengono collegate automaticamente a {company.name} e compaiono nella sua scheda, ricerca e Area di lavoro.
        </p>
        {senderLinks.length > 0 && (
          <table style={{ marginBottom: 10 }}>
            <tbody>
              {senderLinks.map((l) => (
                <tr key={l.id} data-testid={`sender-link-${l.pattern}`}>
                  <td><strong>{l.pattern}</strong>{l.pattern.includes("@") ? "" : <span className="muted" style={{ fontSize: 11 }}> (tutto il dominio)</span>}</td>
                  <td className="muted">{l.note}</td>
                  <td style={{ textAlign: "right" }}>
                    <button className="btn sm danger" onClick={() => delSenderLink(l.id)} data-testid={`sender-link-del-${l.pattern}`}>✕</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className="toolbar" style={{ gap: 8, flexWrap: "wrap" }}>
          <input style={{ maxWidth: 260 }} placeholder="email o dominio (es. maccarilongo.com)" value={slPattern} onChange={(e) => setSlPattern(e.target.value)} data-testid="sender-link-input" />
          <input style={{ maxWidth: 220 }} placeholder="nota (es. consulente paghe)" value={slNote} onChange={(e) => setSlNote(e.target.value)} data-testid="sender-link-note" />
          <button className="btn primary sm" onClick={addSenderLink} data-testid="sender-link-add">+ Collega mittente</button>
        </div>
      </div>

      <div className="panel">
        <h3>Dati anagrafici</h3>
        <div className="grid-2">
          <div><strong>P.IVA:</strong> {company.vat_number || "—"}</div>
          <div><strong>Cod. fiscale:</strong> {company.fiscal_code || "—"}</div>
          <div><strong>Email:</strong> {company.email || "—"}</div>
          <div><strong>PEC:</strong> {company.pec || "—"}</div>
          <div><strong>Telefono:</strong> {company.phone || "—"} <WhatsAppButton phone={company.phone} name={company.name} company={company.name} /></div>
          <div><strong>SDI:</strong> {company.sdi_code || "—"}</div>
          <div><strong>Indirizzo:</strong> {company.address || "—"} {company.city ? `, ${company.city}` : ""} {company.province ? `(${company.province})` : ""} {company.zip_code || ""}</div>
        </div>
        {company.notes && <p style={{ marginTop: 12 }}><strong>Note:</strong> {company.notes}</p>}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12, borderTop: "1px solid var(--border)", paddingTop: 12 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8 }} data-testid="company-review-given-label">
            <input type="checkbox" checked={!!company.review_given} onChange={(e) => toggleReview(e.target.checked)} data-testid="company-review-given" />
            <span>Recensione Google già lasciata <span className="muted">(non riceverà più la richiesta di recensione dopo gli appuntamenti)</span></span>
          </label>
          <span className="spacer" />
          <ReviewRequestButton name={company.name} email={company.email} phone={company.phone} />
        </div>
      </div>

      <CompanyNotesTasks companyId={company.id} />

      <div className="panel" data-testid="company-margin-panel">
        <div className="toolbar">
          <h3 style={{ margin: 0 }}>Margine e ore dedicate {company.reference_year ? <span className="muted" style={{ fontWeight: 400 }}>· anno {company.reference_year}</span> : null}</h3>
          <div className="spacer" />
          <button className="btn sm primary" onClick={openReport} data-testid="send-activity-report-btn">📊 Invia report attività</button>
        </div>
        {company.annual_revenue == null && company.estimated_hours == null ? (
          <p className="muted">Nessun dato di margine ancora calcolato per questo cliente.</p>
        ) : (
          <>
            <div className="grid-2">
              <div><strong>Ricavo annuo:</strong> € {Number(company.annual_revenue || 0).toLocaleString("it-IT", { maximumFractionDigits: 0 })}</div>
              <div><strong>Fatture nell'anno:</strong> {company.invoices_count ?? 0}</div>
              <div data-testid="company-f24-count"><strong>F24 gestiti:</strong> {company.f24_count ?? 0} <span className="muted">(società e persone collegate)</span></div>
              <div data-testid="company-ytd-comms"><strong>Da inizio anno:</strong> {company.emails_ytd ?? company.emails_used ?? 0} email · {company.wa_ytd ?? company.wa_used ?? 0} WhatsApp</div>
            </div>
            <table style={{ marginTop: 10 }}>
              <thead><tr><th>Attività</th><th>Dettaglio</th><th style={{ textAlign: "right" }}>Ore</th></tr></thead>
              <tbody>
                <tr data-testid="hours-docs-row">
                  <td>Caricamento movimenti / documenti</td>
                  <td className="muted">{company.docs_used ?? 0} documenti</td>
                  <td style={{ textAlign: "right" }}>{Number(company.hours_docs || 0).toLocaleString("it-IT", { maximumFractionDigits: 1 })} h</td>
                </tr>
                <tr data-testid="hours-meetings-row">
                  <td>Riunioni / appuntamenti</td>
                  <td className="muted">{company.appts_used ?? 0} appuntamenti</td>
                  <td style={{ textAlign: "right" }}>{Number(company.hours_meetings || 0).toLocaleString("it-IT", { maximumFractionDigits: 1 })} h</td>
                </tr>
                <tr data-testid="hours-emails-row">
                  <td>Email gestite dal cliente</td>
                  <td className="muted">{company.emails_used ?? 0} email</td>
                  <td style={{ textAlign: "right" }}>{Number(company.hours_emails || 0).toLocaleString("it-IT", { maximumFractionDigits: 1 })} h</td>
                </tr>
                <tr data-testid="hours-whatsapp-row">
                  <td>Messaggi WhatsApp</td>
                  <td className="muted">{company.wa_used ?? 0} messaggi</td>
                  <td style={{ textAlign: "right" }}>{Number(company.hours_whatsapp || 0).toLocaleString("it-IT", { maximumFractionDigits: 1 })} h</td>
                </tr>
                <tr data-testid="hours-tasks-row">
                  <td>Incarichi e pratiche</td>
                  <td className="muted">{company.tasks_used ?? 0} incarichi collegati al cliente</td>
                  <td style={{ textAlign: "right" }}>{Number(company.hours_tasks || 0).toLocaleString("it-IT", { maximumFractionDigits: 1 })} h</td>
                </tr>
                <tr style={{ fontWeight: 700, borderTop: "1px solid var(--border)" }}>
                  <td>Ore totali</td>
                  <td className="muted">costo: € {Number(company.estimated_cost || 0).toLocaleString("it-IT", { maximumFractionDigits: 0 })}</td>
                  <td style={{ textAlign: "right" }}>{Number(company.estimated_hours || 0).toLocaleString("it-IT", { maximumFractionDigits: 1 })} h</td>
                </tr>
              </tbody>
            </table>
            {(company.appt_dates || []).length > 0 && (
              <details style={{ marginTop: 10 }} data-testid="appt-dates-details">
                <summary className="muted" style={{ cursor: "pointer" }}>Date appuntamenti svolti ({company.appt_dates.length})</summary>
                <ul style={{ marginTop: 6 }}>
                  {company.appt_dates.map((a, i) => (
                    <li key={i} data-testid="appt-date-item">
                      {a.date ? a.date.split("-").reverse().join("/") : "—"}
                      {a.time ? ` ore ${a.time}` : ""}
                      {a.title ? <span className="muted"> — {a.title}</span> : null}
                      {a.dur ? <span className="muted"> ({a.dur} min)</span> : null}
                    </li>
                  ))}
                </ul>
              </details>
            )}
            <div style={{ marginTop: 12, display: "flex", gap: 24, flexWrap: "wrap" }}>
              <div><span className="muted">Margine annuo</span><div style={{ fontSize: 22, fontWeight: 800, color: (company.annual_margin || 0) >= 0 ? "var(--green)" : "var(--danger)" }} data-testid="company-margin-value">€ {Number(company.annual_margin || 0).toLocaleString("it-IT", { maximumFractionDigits: 0 })}</div></div>
              <div>
                <span className="muted" title="Margine annuo diviso il numero di fatture emesse nell'anno">Guadagno medio per fattura</span>
                <div style={{ fontSize: 22, fontWeight: 800 }}>€ {Number(company.margin_per_purchase || 0).toLocaleString("it-IT", { maximumFractionDigits: 0 })}</div>
                <div className="muted" style={{ fontSize: 11, maxWidth: 280 }}>margine annuo (€ {Number(company.annual_margin || 0).toLocaleString("it-IT", { maximumFractionDigits: 0 })}) diviso le {company.invoices_count || 0} fatture emesse: quanto resta allo Studio, in media, per ogni fattura fatta a questo cliente</div>
              </div>
            </div>
            <p className="muted" style={{ fontSize: 12, marginTop: 10 }}>Le ore si aggiornano automaticamente da documenti, impegni in calendario, email ricevute dal cliente e incarichi creati sulle email (esclusi personali, blocchi e doppioni). Imposta costo orario, minuti/documento, minuti/email e minuti/incarico in Fatture → Impostazioni margine.</p>
          </>
        )}
      </div>

      <div className="panel" data-testid="company-services-panel">
        <h3>Cosa abbiamo venduto</h3>
        {!services || services.categories.length === 0 ? (
          <p className="muted">Nessuna fattura registrata per questo cliente.</p>
        ) : (
          <>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
              {services.categories.map((c) => (
                <span key={c.category} className="chip" style={{ display: "inline-flex", gap: 6, alignItems: "center" }} data-testid="service-chip">
                  {c.category} · <strong>€ {Number(c.total).toLocaleString("it-IT", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</strong>
                  <span className="muted">({c.count})</span>
                </span>
              ))}
            </div>
            <details>
              <summary className="muted" style={{ cursor: "pointer" }}>Dettaglio voci di fattura ({services.items.length})</summary>
              <table style={{ marginTop: 8 }}>
                <thead><tr><th>Data</th><th>Descrizione</th><th>Categorie</th><th>Importo</th></tr></thead>
                <tbody>
                  {services.items.map((it, idx) => (
                    <tr key={idx}>
                      <td style={{ whiteSpace: "nowrap" }}>{it.date || "—"}</td>
                      <td>{it.description}</td>
                      <td className="muted">{(it.categories || []).join(", ")}</td>
                      <td style={{ whiteSpace: "nowrap" }}>€ {Number(it.total).toLocaleString("it-IT", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          </>
        )}
      </div>

      <div className="panel">
        <div className="toolbar"><h3 style={{ margin: 0 }}>Persone collegate</h3><div className="spacer" /><button className="btn sm primary" onClick={() => setLinkModal(true)}>+ Collega persona</button></div>
        <table>
          <thead><tr><th>Persona</th><th>Ruolo nel collegamento</th><th></th></tr></thead>
          <tbody>
            {people.map((pl) => (
              <tr key={pl.link_id}>
                <td><Link to={`/persone/${pl.person.id}`}>{pl.person.last_name} {pl.person.first_name}</Link></td>
                <td>{pl.role || "—"}</td>
                <td><button className="btn sm danger" onClick={() => removeLink(pl.link_id)}>Scollega</button></td>
              </tr>
            ))}
            {people.length === 0 && <tr><td colSpan={3} className="muted">Nessuna persona collegata.</td></tr>}
          </tbody>
        </table>
      </div>

      <CompanyComms companyId={id} company={company} onReload={load} />

      <ClientFiles companyId={id} />
      <SentComms params={`company=${id}`} testid="company-sent-comms" />

      <div className="panel">
        <h3>Progetti</h3>
        <ul>
          {projects.map((p) => <li key={p.id}><Link to={`/progetti/${p.id}`}>{p.name}</Link> <span className="muted">({p.status})</span></li>)}
          {projects.length === 0 && <li className="muted">Nessun progetto.</li>}
        </ul>
      </div>

      <div className="panel">
        <div className="toolbar"><h3 style={{ margin: 0 }}>Archivio documenti</h3><div className="spacer" />
          <input type="file" style={{ maxWidth: 260 }} onChange={(e) => setUpFile(e.target.files[0])} />
          <button className="btn sm primary" onClick={upload} disabled={!upFile}>Carica</button>
        </div>
        <table>
          <thead><tr><th>File</th><th>Origine</th><th>Dimensione</th><th></th></tr></thead>
          <tbody>
            {docs.map((d) => (
              <tr key={d.id}>
                <td><a href={`${BACKEND}/api/documents/${d.id}/download`} target="_blank" rel="noreferrer">{d.filename}</a></td>
                <td>{d.source === "email" ? "Email" : "Caricato"}</td>
                <td>{(d.size / 1024).toFixed(0)} KB</td>
                <td><button className="btn sm danger" onClick={() => removeDoc(d.id)}>Elimina</button></td>
              </tr>
            ))}
            {docs.length === 0 && <tr><td colSpan={4} className="muted">Archivio vuoto.</td></tr>}
          </tbody>
        </table>
      </div>

      {report && (
        <Modal title="Report attività per il cliente" onClose={() => setReport(null)}>
          <p className="muted" style={{ fontSize: 13, marginTop: 0 }}>
            Il cliente riceve solo i numeri delle attività svolte per lui: nessun margine o costo interno.
          </p>
          <div className="field"><label>Destinatario</label>
            <input value={report.to || ""} onChange={(e) => setReport({ ...report, to: e.target.value })} placeholder="email del cliente" data-testid="report-to-input" />
          </div>
          <div className="field"><label>Oggetto</label>
            <input value={report.subject || ""} readOnly style={{ opacity: .8 }} />
          </div>
          <div className="field"><label>Anteprima</label>
            <iframe title="anteprima report" sandbox="" srcDoc={report.html} style={{ width: "100%", height: 300, border: "1px solid var(--border)", borderRadius: 8, background: "#fff" }} data-testid="report-preview" />
          </div>
          <div className="modal-actions">
            <button className="btn" onClick={() => setReport(null)}>Annulla</button>
            <button className="btn primary" onClick={sendReport} disabled={repBusy || !report.to} data-testid="report-send-btn">{repBusy ? "Invio…" : "Invia al cliente"}</button>
          </div>
        </Modal>
      )}

      {mergeModal && (
        <Modal title="Unisci in un'altra azienda" onClose={() => setMergeModal(false)}>
          <p className="muted" style={{ marginTop: 0 }}>Tutto ciò che è collegato a <strong>{company.name}</strong> (fatture e fatturato, email, F24, cartelle, registrazioni, documenti, referenti) verrà spostato sull'azienda scelta. Poi <strong>questa azienda verrà eliminata</strong> dalla lista clienti. Operazione non annullabile.</p>
          <div className="field"><label>Azienda di destinazione</label>
            <select value={mergeTarget} onChange={(e) => setMergeTarget(e.target.value)} data-testid="merge-target-select">
              <option value="">— seleziona —</option>
              {mergeCompanies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: 8, margin: "10px 0" }}>
            <input type="checkbox" checked={mergeCreatePerson} onChange={(e) => setMergeCreatePerson(e.target.checked)} data-testid="merge-create-person" />
            <span>Crea una persona referente con nome, email e telefono di questa azienda</span>
          </label>
          <div className="modal-actions">
            <button className="btn" onClick={() => setMergeModal(false)}>Annulla</button>
            <button className="btn primary" onClick={doMerge} disabled={!mergeTarget || merging} data-testid="merge-confirm-btn">{merging ? "Unione in corso…" : "Unisci"}</button>
          </div>
        </Modal>
      )}

      {linkModal && (
        <Modal title="Collega una persona" onClose={() => setLinkModal(false)}>
          <div className="field"><label>Persona</label>
            <select value={linkPerson} onChange={(e) => setLinkPerson(e.target.value)}>
              <option value="">— seleziona —</option>
              {allPersons.map((p) => <option key={p.id} value={p.id}>{p.last_name} {p.first_name}</option>)}
            </select>
          </div>
          <div className="field"><label>Ruolo nel collegamento</label><input placeholder="es. Consulente paghe" value={linkRole} onChange={(e) => setLinkRole(e.target.value)} /></div>
          <div className="modal-actions">
            <button className="btn" onClick={() => setLinkModal(false)}>Annulla</button>
            <button className="btn primary" onClick={addLink} disabled={!linkPerson}>Collega</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
